<template>
  <div id="app">
    <header class="site-header">
      <nav class="navbar">
        <div class="nav-container">
          <router-link to="/" class="nav-logo">
            <img src="@/assets/UVEFFECTS-stamp.png" alt="UV Effects" class="logo-image" />
          </router-link>
          
          <!-- Hamburger Menu Button -->
          <button class="hamburger" @click="toggleMenu" :class="{ active: isMenuOpen }" aria-label="Toggle navigation menu">
            <span class="hamburger-line"></span>
            <span class="hamburger-line"></span>
            <span class="hamburger-line"></span>
          </button>
          
          <ul class="nav-menu" :class="{ active: isMenuOpen }">
            <li class="nav-item">
              <router-link to="/pedals" class="nav-link" @click="handleNavClick">Pedals</router-link>
            </li>
            <li class="nav-item">
              <router-link to="/components" class="nav-link" @click="handleNavClick">Components</router-link>
            </li>
            <li class="nav-item">
              <router-link to="/about" class="nav-link" @click="handleNavClick">About</router-link>
            </li>
            <li class="nav-item">
              <a href="https://reverb.com/shop/uveffects" target="_blank" class="nav-link shop-link" @click="handleNavClick">Shop on Reverb</a>
            </li>
          </ul>
        </div>
      </nav>
    </header>

    <main>
      <router-view/>
    </main>

    <footer class="site-footer">
      <div class="footer-container">
        <div class="footer-content">
          <img src="@/assets/UV-stamp.jpg" alt="UV Effects Stamp" class="footer-logo" />
          <h3>UV Effects | Boutique Guitar Pedals Built in Colorado</h3>
          <p>All UV Effects pedals are unique and built by hand using high-quality NOS silicon and germanium components.</p>
          
          <div class="social-links">
            <a href="https://www.instagram.com/uveffects/" target="_blank">Instagram</a>
            <a href="https://www.youtube.com/@UVEffects" target="_blank">YouTube</a>
            <a href="https://reverb.com/shop/uveffects" target="_blank">Reverb Store</a>
          </div>
        </div>
        
        <div class="footer-bottom">
          <p>&copy; 2025, UV Effects</p>
          <div class="footer-links">
            <router-link to="/contact" @click="scrollToTop">Contact</router-link>
            <router-link to="/privacy" @click="scrollToTop">Privacy Policy</router-link>
            <router-link to="/terms" @click="scrollToTop">Terms of Service</router-link>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
export default {
  name: 'App',
  data() {
    return {
      isMenuOpen: false
    }
  },
  methods: {
    scrollToTop() {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen;
    },
    closeMenu() {
      this.isMenuOpen = false;
      // Scroll to top when menu item is clicked
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    handleNavClick() {
      // Close mobile menu if open and scroll to top
      if (this.isMenuOpen) {
        this.closeMenu();
      } else {
        // Desktop navigation - just scroll to top
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  },
  watch: {
    '$route'() {
      // Scroll to top on route changes
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }
}
</script>

<style>
:root {
  --text-primary: #242424;
  --text-secondary: #363636;
  --gradient-primary: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

#app {
  font-family: "Roboto Mono", monospace;
  font-optical-sizing: auto;
  font-weight: 400;
  font-style: normal;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: var(--text-primary);
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

h1, h2, h3, h4, h5, h6 {
  font-weight: 400;
  color: var(--text-primary);
}

/* Header Styles */
.site-header {
  background: #fff;
  /* border-bottom: 1px solid #e1e1e1; */
  position: sticky;
  top: 0;
  z-index: 100;
}

.nav-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 80px;
}

.nav-logo {
  text-decoration: none;
  display: flex;
  align-items: center;
}

.logo-image {
  height: 60px;
  width: auto;
  transition: opacity 0.2s ease;
}

.nav-logo:hover .logo-image {
  opacity: 0.8;
}

.nav-menu {
  display: flex;
  list-style: none;
  gap: 30px;
  align-items: center;
}

.nav-link {
  text-decoration: none;
  color: var(--text-primary);
  font-weight: 400;
  transition: color 0.2s ease;
}

.nav-link:hover {
  color: var(--text-secondary);
}

.nav-link.router-link-active {
  color: #000;
  font-weight: 400;
  text-decoration: underline;
}

.shop-link {
  position: relative;
  padding: 10px 20px;
  border-radius: 5px;
  background: #fff;
  background-clip: padding-box;
  border: solid 2px transparent;
  transition: all 0.3s ease;
  color: #2448e9;
}

.shop-link:before {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: -1;
  margin: -2px;
  border-radius: inherit;
  background: var(--gradient-primary);
}

.shop-link:hover {
  background: var(--gradient-primary);
  color: #fff;
}

/* Hamburger Menu */
.hamburger {
  display: none;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 30px;
  height: 30px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  z-index: 1001;
}

.hamburger-line {
  width: 25px;
  height: 3px;
  background-color: var(--text-secondary);
  margin: 3px 0;
  transition: 0.3s;
  transform-origin: center;
}

/* Hamburger to X animation */
.hamburger.active .hamburger-line:nth-child(1) {
  transform: rotate(45deg) translate(6px, 6px);
}

.hamburger.active .hamburger-line:nth-child(2) {
  opacity: 0;
}

.hamburger.active .hamburger-line:nth-child(3) {
  transform: rotate(-45deg) translate(6px, -6px);
}

.shop-button {
  position: relative;
  padding: 15px 20px;
  border-radius: 5px;
  background: #fff;
  background-clip: padding-box;
  border: solid 2px transparent;
  transition: all 0.3s ease;
  color: #2448e9;
  text-decoration: none;
  z-index: 10;
}

.shop-button:before {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: -1;
  margin: -2px;
  border-radius: inherit;
  background: var(--gradient-primary);
}

.shop-button:hover {
  background: var(--gradient-primary);
  color: #fff;
}

/* Main Content */
main {
  flex: 1;
}

/* Footer Styles */
.site-footer {
  border-top: 1px solid #e1e1e1;
  padding: 40px 20px 20px;
  margin-top: auto;
}

.footer-container {
  max-width: 1200px;
  margin: 0 auto;
}

.footer-content {
  text-align: center;
  margin-bottom: 30px;
}

.footer-logo {
  width: 240px;
  height: auto;
  margin-bottom: 20px;
  opacity: 0.8;
}

.footer-content h3 {
  margin-bottom: 10px;
  font-size: 20px;
}

.footer-content p {
  color: var(--text-secondary);
  margin-bottom: 20px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

.social-links {
  display: flex;
  justify-content: center;
  gap: 20px;
}

.social-links a {
  color: var(--text-primary);
  text-decoration: none;
  font-weight: 400;
}

.social-links a:hover {
  color: var(--text-secondary);
}

.footer-bottom {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 20px;
  border-top: 1px solid #e1e1e1;
  font-size: 14px;
  color: var(--text-secondary);
}

.footer-links {
  display: flex;
  gap: 20px;
}

.footer-links a {
  color: var(--text-secondary);
  text-decoration: none;
}

.footer-links a:hover {
  color: var(--text-primary);
}

/* Responsive Design */
@media (max-width: 768px) {
  .nav-container {
    justify-content: space-between;
    height: 70px;
    padding: 15px 20px;
  }
  
  .hamburger {
    display: flex;
  }
  
  .nav-menu {
    position: fixed;
    top: 70px;
    left: 0;
    width: 100%;
    height: calc(100vh - 70px);
    background: #fff;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    padding-top: 50px;
    gap: 30px;
    transform: translateX(-100%);
    transition: transform 0.3s ease-in-out;
    z-index: 1000;
    box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  }
  
  .nav-menu.active {
    transform: translateX(0);
  }
  
  .nav-item {
    width: 100%;
    text-align: center;
  }
  
  .nav-link {
    display: block;
    padding: 15px 20px;
    font-size: 1.2rem;
    width: 100%;
  }
  
  .shop-link {
    margin: 20px;
    padding: 15px 30px;
    border-radius: 25px;
    width: auto;
  }
  
  .footer-bottom {
    flex-direction: column;
    gap: 15px;
  }
  
  .social-links {
    flex-wrap: wrap;
  }
}
</style>
